/**
 * 날짜/시간 포맷팅 유틸리티
 * Date Formatting Utilities
 *
 * ISO 8601 형식의 날짜/시간을 UI 언어별 표준 Intl 형식으로 변환하는 함수들
 */

import { normalizeLocaleTag } from '@/lib/i18nLocale';
import {
	formatLocaleDate,
	formatLocaleDateShort,
	formatLocaleDateTime,
	normalizeDateString,
	localeFormatOptionsDateTimeHms24,
} from '@/lib/localeFormat';
import i18n from '@/i18n';

/** 날짜·상대시간: 한국어 UI(ko)만 한국식, 그 외는 미국식·영어 상대시간 */
function isKoreanUiDateLocale(locale?: string): boolean {
	const lang = locale ?? i18n?.language ?? '';
	return normalizeLocaleTag(typeof lang === 'string' ? lang : '') === 'ko';
}

/**
 * ISO 8601 날짜를 UI 언어별 표준 날짜 형식으로 변환
 */
export const formatDate = (isoDate: string | null | undefined, locale?: string | null): string => {
	return formatLocaleDate(isoDate, locale);
};

/**
 * ISO 8601 날짜를 UI 언어별 짧은 날짜 형식으로 변환
 */
export const formatDateShort = (isoDate: string | null | undefined, locale?: string | null): string => {
	return formatLocaleDateShort(isoDate, locale);
};

/**
 * ISO 8601(또는 날짜 접두) → 달력일만 `YYYY-MM-DD` (블로그 게시일 등, 로케일 무관)
 */
export function formatIsoDateToYmd(iso: string | null | undefined): string | undefined {
	if (!iso?.trim()) return undefined;
	const s = iso.trim();
	const head = s.slice(0, 10);
	if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
	try {
		const date = new Date(normalizeDateString(s));
		if (Number.isNaN(date.getTime())) return undefined;
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, '0');
		const d = String(date.getDate()).padStart(2, '0');
		return `${y}-${m}-${d}`;
	} catch {
		return undefined;
	}
}

/**
 * ISO 8601 날짜를 UI 언어별 날짜+시간 형식으로 변환
 */
export const formatDateTime = (isoDate: string | null | undefined, locale?: string | null): string => {
	return formatLocaleDateTime(isoDate, locale);
};

/**
 * ISO 8601 날짜를 UI 언어별 날짜+시간+초 형식으로 변환
 */
export const formatDateTimeWithSeconds = (isoDate: string | null | undefined, locale?: string | null): string => {
	return formatLocaleDateTime(isoDate, locale, localeFormatOptionsDateTimeHms24);
};

/**
 * ISO 8601 날짜를 한국어 날짜+시간 형식으로 변환
 * @param isoDate ISO 8601 형식 날짜 문자열
 * @returns "2025년 1월 15일 10:30" 형식 문자열
 * 
 * @example
 * formatDateTimeKorean("2025-01-15T10:30:00Z") // "2025년 1월 15일 10:30"
 */
export const formatDateTimeKorean = (isoDate: string | null | undefined): string => {
	if (!isoDate) return '-';
	
	try {
		const date = new Date(normalizeDateString(isoDate));
		if (isNaN(date.getTime())) return '-';
		
		return formatLocaleDateTime(date, 'ko');
	} catch {
		return '-';
	}
};

const RELATIVE_KO = {
	today: '오늘',
	tomorrow: '내일',
	yesterday: '어제',
	justNow: '방금 전',
	daysLater: (n: number) => `${n}일 후`,
	weeksLater: (n: number) => `${n}주 후`,
	monthsLater: (n: number) => `${n}개월 후`,
	yearsLater: (n: number) => `${n}년 후`,
	minutesAgo: (n: number) => `${n}분 전`,
	hoursAgo: (n: number) => `${n}시간 전`,
	daysAgo: (n: number) => `${n}일 전`,
	weeksAgo: (n: number) => `${n}주 전`,
	monthsAgo: (n: number) => `${n}개월 전`,
	yearsAgo: (n: number) => `${n}년 전`,
};
const RELATIVE_EN = {
	today: 'Today',
	tomorrow: 'Tomorrow',
	yesterday: 'Yesterday',
	justNow: 'Just now',
	daysLater: (n: number) => `in ${n} day${n === 1 ? '' : 's'}`,
	weeksLater: (n: number) => `in ${n} week${n === 1 ? '' : 's'}`,
	monthsLater: (n: number) => `in ${n} month${n === 1 ? '' : 's'}`,
	yearsLater: (n: number) => `in ${n} year${n === 1 ? '' : 's'}`,
	minutesAgo: (n: number) => `${n} min ago`,
	hoursAgo: (n: number) => `${n} hr ago`,
	daysAgo: (n: number) => `${n} day${n === 1 ? '' : 's'} ago`,
	weeksAgo: (n: number) => `${n} week${n === 1 ? '' : 's'} ago`,
	monthsAgo: (n: number) => `${n} month${n === 1 ? '' : 's'} ago`,
	yearsAgo: (n: number) => `${n} year${n === 1 ? '' : 's'} ago`,
};

/**
 * 상대 시간 표시. 한국: "3일 전", "방금 전" / 영어: "3 days ago", "Just now"
 */
export const formatRelativeTime = (isoDate: string | null | undefined, locale?: string): string => {
	if (!isoDate) return '-';

	try {
		const date = new Date(normalizeDateString(isoDate));
		if (isNaN(date.getTime())) return '-';

		const R = !isKoreanUiDateLocale(locale) ? RELATIVE_EN : RELATIVE_KO;
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();

		if (diffMs < 0) {
			const futureDiffMs = Math.abs(diffMs);
			const futureDiffDays = Math.floor(futureDiffMs / (1000 * 60 * 60 * 24));
			if (futureDiffDays === 0) return R.today;
			if (futureDiffDays === 1) return R.tomorrow;
			if (futureDiffDays < 7) return R.daysLater(futureDiffDays);
			if (futureDiffDays < 30) return R.weeksLater(Math.floor(futureDiffDays / 7));
			if (futureDiffDays < 365) return R.monthsLater(Math.floor(futureDiffDays / 30));
			return R.yearsLater(Math.floor(futureDiffDays / 365));
		}

		const diffSeconds = Math.floor(diffMs / 1000);
		const diffMinutes = Math.floor(diffSeconds / 60);
		const diffHours = Math.floor(diffMinutes / 60);
		const diffDays = Math.floor(diffHours / 24);

		if (diffSeconds < 60) return R.justNow;
		if (diffMinutes < 60) return R.minutesAgo(diffMinutes);
		if (diffHours < 24) return R.hoursAgo(diffHours);
		if (diffDays === 0) return R.today;
		if (diffDays === 1) return R.yesterday;
		if (diffDays < 7) return R.daysAgo(diffDays);
		if (diffDays < 30) return R.weeksAgo(Math.floor(diffDays / 7));
		if (diffDays < 365) return R.monthsAgo(Math.floor(diffDays / 30));
		return R.yearsAgo(Math.floor(diffDays / 365));
	} catch {
		return '-';
	}
};

/**
 * 두 날짜 사이의 일수 계산
 * @param startDate 시작 날짜 (ISO 8601)
 * @param endDate 종료 날짜 (ISO 8601)
 * @returns 일수 (양수)
 * 
 * @example
 * getDaysBetween("2025-01-15T00:00:00Z", "2025-01-20T00:00:00Z") // 5
 */
export const getDaysBetween = (
	startDate: string | null | undefined, 
	endDate: string | null | undefined
): number => {
	if (!startDate || !endDate) return 0;
	
	try {
		const start = new Date(startDate);
		const end = new Date(endDate);
		
		if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
		
		const diffMs = Math.abs(end.getTime() - start.getTime());
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
		
		return diffDays;
	} catch {
		return 0;
	}
};

/**
 * 날짜가 과거인지 확인
 * @param isoDate ISO 8601 형식 날짜 문자열
 * @returns true if 과거, false if 현재/미래
 * 
 * @example
 * isPastDate("2024-01-15T00:00:00Z") // true (2026년 기준)
 */
export const isPastDate = (isoDate: string | null | undefined): boolean => {
	if (!isoDate) return false;
	
	try {
		const date = new Date(isoDate);
		if (isNaN(date.getTime())) return false;
		
		return date.getTime() < new Date().getTime();
	} catch {
		return false;
	}
};

/**
 * 날짜가 미래인지 확인
 * @param isoDate ISO 8601 형식 날짜 문자열
 * @returns true if 미래, false if 현재/과거
 * 
 * @example
 * isFutureDate("2027-01-15T00:00:00Z") // true (2026년 기준)
 */
export const isFutureDate = (isoDate: string | null | undefined): boolean => {
	if (!isoDate) return false;
	
	try {
		const date = new Date(isoDate);
		if (isNaN(date.getTime())) return false;
		
		return date.getTime() > new Date().getTime();
	} catch {
		return false;
	}
};

/**
 * 현재 날짜를 ISO 8601 형식으로 반환
 * @returns ISO 8601 형식 문자열
 * 
 * @example
 * getCurrentDateISO() // "2025-01-23T10:30:00.000Z"
 */
export const getCurrentDateISO = (): string => {
	return new Date().toISOString();
};

/**
 * 현재 날짜를 YYYY-MM-DD 형식으로 반환
 * @returns YYYY-MM-DD 형식 문자열
 * 
 * @example
 * getCurrentDate() // "2025-01-23"
 */
export const getCurrentDate = (): string => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const day = String(now.getDate()).padStart(2, '0');
	
	return `${year}-${month}-${day}`;
};

/**
 * N일 전/후 날짜를 ISO 8601 형식으로 반환
 * @param days 일수 (양수: 미래, 음수: 과거)
 * @returns ISO 8601 형식 문자열
 * 
 * @example
 * getDateOffset(7) // 7일 후
 * getDateOffset(-30) // 30일 전
 */
export const getDateOffset = (days: number): string => {
	const date = new Date();
	date.setDate(date.getDate() + days);
	return date.toISOString();
};

/**
 * N일 전/후 날짜를 YYYY-MM-DD 형식으로 반환
 * @param days 일수 (양수: 미래, 음수: 과거)
 * @returns YYYY-MM-DD 형식 문자열
 * 
 * @example
 * getDateOffsetShort(7) // "2025-01-30" (오늘이 2025-01-23인 경우)
 * getDateOffsetShort(-30) // "2024-12-24"
 */
export const getDateOffsetShort = (days: number): string => {
	const date = new Date();
	date.setDate(date.getDate() + days);
	
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	
	return `${year}-${month}-${day}`;
};
